import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { assets, SITE } from "@/lib/config";
import { MaskedLine } from "./Reveal";
import { MonoLabel, StatusDot, btnGhost, btnOutline, btnPrimary } from "./primitives";
import { STORY_FLOW } from "@/lib/demo-data";

/** Cinematic hero — masked line reveal, parallax artwork, HUD chrome. */
export function Hero({ ready }: { ready: boolean }) {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 130]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, reduced ? 1 : 0.2]);

  return (
    <section ref={ref} className="relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden" data-testid="hero-section">
      {/* Background: supplied artwork, deliberately cropped to the art side */}
      <motion.div className="absolute inset-0" style={reduced ? undefined : { y, opacity }}>
        <img
          src={assets.art}
          alt=""
          aria-hidden="true"
          className="absolute right-0 top-0 h-full w-full object-cover object-[80%_center] opacity-[0.38] sm:opacity-50 lg:w-[68%] lg:opacity-60"
          loading="eager"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = assets.artFallback;
          }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,#02050A_28%,rgba(2,5,10,0.6)_55%,rgba(2,5,10,0.25)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(0deg,#02050A_2%,transparent_35%,transparent_75%,#02050A_100%)]" />
      </motion.div>
      <div className="bg-grid absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_70%_60%_at_35%_40%,black,transparent)]" />

      <div className="container-t relative z-10 pb-24 pt-32">
        <motion.div
          initial={reduced ? undefined : { opacity: 0 }}
          animate={reduced ? undefined : { opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap items-center gap-x-6 gap-y-2"
        >
          <StatusDot tone="success" label="Proof of Consequence" />
          <MonoLabel className="text-[9px]">SYS.VER 1.0 · OUTCOME LAYER</MonoLabel>
        </motion.div>

        <h1 className="mt-8" data-testid="hero-title">
          <MaskedLine show={ready} delay={0.15}>
            <span className="font-display text-xl font-semibold uppercase tracking-[0.06em] text-dim sm:text-2xl lg:text-3xl">
              Don’t prove that you worked.
            </span>
          </MaskedLine>
          <MaskedLine show={ready} delay={0.32}>
            <span className="block font-display text-[2.6rem] font-bold uppercase leading-[0.98] tracking-tight text-ink sm:text-6xl lg:text-7xl">
              Prove that your work
            </span>
          </MaskedLine>
          <MaskedLine show={ready} delay={0.48}>
            <span className="block font-display text-[2.6rem] font-bold uppercase leading-[0.98] tracking-tight text-cyan glow-cyan sm:text-6xl lg:text-7xl">
              mattered.
            </span>
          </MaskedLine>
        </h1>

        <motion.div
          initial={reduced ? undefined : { opacity: 0, y: 16 }}
          animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          transition={{ duration: 0.7, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mt-7 font-mono text-xs uppercase tracking-[0.3em] text-electric sm:text-sm">
            [ The Outcome Layer for Autonomous Economies ]
          </p>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-dim sm:text-lg">
            Consequence Protocol transforms actions into measurable, verifiable outcomes — creating a new layer of
            reputation, trust, and economic access.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4" data-testid="hero-cta-row">
            <Link to="/terminal" data-testid="hero-enter-terminal" className={btnPrimary}>
              Enter Terminal
            </Link>
            <Link to="/protocol" data-testid="hero-read-protocol" className={btnOutline}>
              Read the Protocol
            </Link>
            <Link to="/outcomes" data-testid="hero-view-outcomes" className={btnGhost}>
              View Outcomes <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>

          <p className="mt-10 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-dim/70">
            {STORY_FLOW.join("  ▸  ")}
          </p>
        </motion.div>
      </div>

      {/* Floating HUD chip */}
      <motion.div
        initial={reduced ? undefined : { opacity: 0, x: 24 }}
        animate={ready ? { opacity: 1, x: 0 } : { opacity: 0, x: 24 }}
        transition={{ duration: 0.7, delay: 1, ease: [0.16, 1, 0.3, 1] }}
        className="panel scanlines hud-corners absolute bottom-10 right-8 z-10 hidden w-60 p-4 lg:block"
      >
        <div className="flex items-center justify-between">
          <MonoLabel className="text-[9px]">Verification Layer</MonoLabel>
          <StatusDot tone="success" label="Online" />
        </div>
        <div className="mt-3 space-y-2 font-mono text-[11px]">
          <div className="flex justify-between text-dim">
            <span>PROTOCOL STATE</span>
            <span className="text-cyan">CONCEPT</span>
          </div>
          <div className="flex justify-between text-dim">
            <span>REGISTRY MODE</span>
            <span className="text-warn">DEMO</span>
          </div>
          <div className="flex justify-between text-dim">
            <span>CA</span>
            <span className="text-cyan">
              {SITE.contractAddress.slice(0, 6)}…{SITE.contractAddress.slice(-4)}
            </span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
